import React, { useEffect, useMemo } from 'react';
import Immutable from 'immutable';
import { useSelector, useDispatch } from 'react-redux';

import {
  Node,
  Taxonomy as TaxonomyTerm,
  entityGetByPath,
  referenceLoadingReset,
  entityReset,
} from '@uncinc/react-drupal-core';

import { JOYRIDE_NODE_TYPE, ONBOARDING_NODE_TYPE } from '../../config/constants';
import { usePrevious } from '../../hooks/usePrevious';
import { WebformWrapper } from '../../implementations';
import PageNotFound from '../../pages/NotFound';
import PageForbidden from '../../pages/Forbidden';
import Page from '../Page';

const Entity = () => {
  const dispatch = useDispatch();
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  const path = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const entityStatus = useSelector((state) => state.getIn(['entity', 'status']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const isLoggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], null));
  const prevPath = usePrevious(path);

  // Child entity props.
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Immutable.Map()));
  const childEntityIsFront = childEntity.get('is_front') === true;
  const childEntityType = childEntity.getIn(['type', 0, 'target_id'], '');
  const childEntityTypeIsBasicPage = childEntityType === 'basic_page';

  // When this component unmounts, we are most likely navigating to a non-entity
  // route. In order to prevent old entity data from being rendered on subsequent
  // entity routes, we want to reset the current entity in the state.
  useEffect(() => {
    return () => {
      dispatch(entityReset());
    };
  }, []);

  useEffect(() => {
    if (!entityType || !entityId) {
      dispatch(entityGetByPath(path));
    }

    if (path !== prevPath) {
      dispatch(referenceLoadingReset());
      dispatch(entityGetByPath(path));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, path]);

  const renderPage = () => {
    if ((!entityType || !entityId) && entityExists) {
      return null;
    }

    if (entityStatus === 404) {
      return (
        <PageNotFound />
      );
    }

    if (entityStatus === 403) {
      return (
        <PageForbidden />
      );
    }

    return (
      <>
        {entityType === 'node' && (
          <Node root id={entityId} />
        )}
        {entityType === 'taxonomy_term' && (
          <TaxonomyTerm root id={entityId} />
        )}
        {entityType === 'webform' && (
          <WebformWrapper root id={entityId} title={childEntity.getIn(['info', 'title'], '')} />
        )}
      </>
    );
  };

  const hideChrome = useMemo(() => (
    isLoggedIn && (
      childEntityIsFront
      || childEntityType === ONBOARDING_NODE_TYPE
      || entityType === 'webform'
      || childEntityType === JOYRIDE_NODE_TYPE
    )
  ), [isLoggedIn, childEntityIsFront, childEntityType]);

  const hideMenuMain = useMemo(() => (
    isLoggedIn && childEntityType === ONBOARDING_NODE_TYPE
  ), [isLoggedIn, childEntityType]);

  return (
    <Page
      title={childEntity.getIn(['title', 0, 'value'], '')}
      hideChrome={hideChrome}
      hideMenuMain={hideMenuMain}
      showFooter={childEntityIsFront || (childEntityTypeIsBasicPage && !isLoggedIn)}
      hideHeader={entityType === 'webform' || childEntityType === JOYRIDE_NODE_TYPE}
      className={childEntityType}
    >
      {renderPage()}
    </Page>
  );
};

export default Entity;
